import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithMsalAuth } from "./baseApi";
import {
  AddUserResponse,
  CustomerCreateUserRequest,
  CustomerCurrentUserQuery,
  CustomerDeleteUserRequest,
  CustomerDeleteUserResponse,
  CustomerEmailCheckQuery,
  CustomerEmailCheckResponse,
  CustomerOrganisationDiscoveryResponse,
  CustomerUpdateUserRequest,
  CustomerUserDetailQuery,
  CustomerUsersQuery,
  GetUsersResponse,
  UserModel,
} from "@shared/models/userModels";

const controllerName = '/user';

const userManagementTag = 'CustomerUsers' as const;

export const buildCurrentUserOrganisationsQuery = () => ({
  url: `${controllerName}/current/organisations`,
  method: 'GET',
});

export const buildCurrentUserQuery = ({ organisationId }: CustomerCurrentUserQuery) => ({
  url: `${controllerName}/current`,
  params: { organisation_id: organisationId },
});

export const buildCustomerUserDetailQuery = ({
  userId,
  organisationId,
}: CustomerUserDetailQuery) => ({
  url: `${controllerName}/`,
  params: { user_id: userId, organisation_id: organisationId },
});

const buildUsersQuery = ({
  organisationId,
  pageSize,
  pageNumber,
  name,
  email,
  ssoUsers,
  sortBy,
  sortOrder,
}: CustomerUsersQuery) => {
  const query = new URLSearchParams({ organisation_id: organisationId });
  if (name) query.set('name', name);
  if (email) query.set('email', email);
  if (ssoUsers !== undefined) query.set('sso_users', String(ssoUsers));
  if (sortBy) query.set('sort_by', sortBy);
  if (sortOrder) query.set('sort_order', sortOrder);
  return `${controllerName}/users/${pageSize}/${pageNumber}?${query.toString()}`;
};

export const buildCustomerUsersQuery = buildUsersQuery;

export const usersApi = createApi({
  reducerPath: 'usersApi',
  baseQuery: baseQueryWithMsalAuth,
  tagTypes: [userManagementTag],
  endpoints: (builder) => ({
    getCurrentUserOrganisations: builder.query<CustomerOrganisationDiscoveryResponse, void>({
      query: buildCurrentUserOrganisationsQuery,
    }),
    getCurrentUser: builder.query<UserModel, CustomerCurrentUserQuery>({
      query: buildCurrentUserQuery,
      // A self-edit can change the active role; refresh permission evaluation
      // through the existing user-mutation invalidation, just like user details.
      providesTags: [userManagementTag],
    }),
    getCustomerUsers: builder.query<GetUsersResponse, CustomerUsersQuery>({
      query: buildUsersQuery,
      providesTags: [userManagementTag],
    }),
    getCustomerUser: builder.query<UserModel, CustomerUserDetailQuery>({
      query: buildCustomerUserDetailQuery,
      providesTags: (_result, _error, { userId }) => [{ type: userManagementTag, id: userId }],
    }),
    checkCustomerUserEmail: builder.query<CustomerEmailCheckResponse, CustomerEmailCheckQuery>({
      query: ({ email, organisationId }) => ({
        url: `${controllerName}/check-user`,
        params: { email, organisation_id: organisationId },
      }),
    }),
    createCustomerUser: builder.mutation<AddUserResponse, CustomerCreateUserRequest>({
      query: (body) => ({ url: `${controllerName}/`, method: 'POST', body }),
      invalidatesTags: [userManagementTag],
    }),
    updateCustomerUser: builder.mutation<void, CustomerUpdateUserRequest>({
      query: (body) => ({ url: `${controllerName}/`, method: 'PUT', body }),
      invalidatesTags: (_result, _error, { user_id }) => [
        userManagementTag,
        { type: userManagementTag, id: user_id },
      ],
    }),
    deleteCustomerUser: builder.mutation<CustomerDeleteUserResponse, CustomerDeleteUserRequest>({
      query: ({ userId, organisationId }) => ({
        url: `${controllerName}/${userId}`,
        method: 'DELETE',
        params: { organisation_id: organisationId },
      }),
      invalidatesTags: [userManagementTag],
    }),
  }),
});
 
export const {
  useGetCurrentUserOrganisationsQuery,
  useGetCurrentUserQuery,
  useGetCustomerUsersQuery,
  useGetCustomerUserQuery,
  useLazyCheckCustomerUserEmailQuery,
  useCreateCustomerUserMutation,
  useUpdateCustomerUserMutation,
  useDeleteCustomerUserMutation,
} = usersApi;
